// src/components/ApprovalDialogs.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import { FleetVehicle } from '../types';

interface Vehicle {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  year: number;
  category: string;
  seatingCapacity?: number;
  status: string;
}

interface SupervisorApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: (comments: string) => void;
  requestDetails: {
    purpose: string;
    destination: string;
    requester: string;
    passengerCount: number;
  };
  loading?: boolean;
}

interface FleetApprovalDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: (vehicleId: string, driverName: string, comments: string) => void;
  requestDetails: {
    purpose: string;
    destination: string;
    requester: string;
    passengerCount: number;
    startDate: string;
    endDate: string;
    category?: string;
  };
  availableVehicles: FleetVehicle[];
  loading?: boolean;
}

interface RejectionDialogProps {
  open: boolean;
  onClose: () => void;
  onReject: (reason: string) => void;
  requestDetails: {
    purpose: string;
    requester: string;
  };
  loading?: boolean;
}

export const SupervisorApprovalDialog: React.FC<SupervisorApprovalDialogProps> = ({
  open,
  onClose,
  onApprove,
  requestDetails,
  loading = false
}) => {
  const [comments, setComments] = useState('');

  const handleApprove = () => {
    onApprove(comments || 'Approved');
    setComments('');
  };

  const handleClose = () => {
    setComments('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Approve Request</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            You are approving this request for vehicle assignment
          </Alert>
          
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Request Details
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Requester:</strong> {requestDetails.requester}
                </Typography>
                <Typography variant="body2">
                  <strong>Purpose:</strong> {requestDetails.purpose}
                </Typography>
                <Typography variant="body2">
                  <strong>Destination:</strong> {requestDetails.destination}
                </Typography>
                <Typography variant="body2">
                  <strong>Passengers:</strong> {requestDetails.passengerCount}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments (Optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any approval notes or instructions..."
            disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleApprove} 
          variant="contained" 
          color="success"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Approving...' : 'Approve Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const FleetApprovalDialog: React.FC<FleetApprovalDialogProps> = ({
  open,
  onClose,
  onApprove,
  requestDetails,
  availableVehicles,
  loading = false
}) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (open && availableVehicles.length === 1) {
      setSelectedVehicleId(availableVehicles[0].id);
    }
  }, [open, availableVehicles]);

  const handleApprove = () => {
    if (!selectedVehicleId) return;
    onApprove(selectedVehicleId, driverName || 'TBD', comments || 'Vehicle assigned');
    handleReset();
  };

  const handleReset = () => {
    setSelectedVehicleId('');
    setDriverName('');
    setComments('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const selectedVehicle = availableVehicles.find(v => v.id === selectedVehicleId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Assign Vehicle</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Request Details
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Requester:</strong> {requestDetails.requester}
                </Typography>
                <Typography variant="body2">
                  <strong>Purpose:</strong> {requestDetails.purpose}
                </Typography>
                <Typography variant="body2">
                  <strong>Destination:</strong> {requestDetails.destination}
                </Typography>
                <Typography variant="body2">
                  <strong>Period:</strong> {new Date(requestDetails.startDate).toLocaleString()} - {new Date(requestDetails.endDate).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Passengers:</strong> {requestDetails.passengerCount}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {availableVehicles.length === 0 ? (
            <Alert severity="error" icon={<WarningIcon />}>
              No vehicles available for the requested time slot. Please check the calendar or reject this request.
            </Alert>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Vehicle *</InputLabel>
                <Select
                  value={selectedVehicleId}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                  label="Select Vehicle *"
                  disabled={loading}
                >
                  {availableVehicles.map((vehicle) => (
                    <MenuItem key={vehicle.id} value={vehicle.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <CarIcon fontSize="small" />
                        <Typography>
                          {vehicle.plateNumber} - {vehicle.make} {vehicle.model} ({vehicle.year})
                        </Typography>
                        <Box sx={{ ml: 'auto' }}>
                          <Chip 
                            size="small" 
                            label={vehicle.category} 
                            variant="outlined"
                          />
                          {vehicle.seatingCapacity && (
                            <Chip 
                              size="small" 
                              label={`${vehicle.seatingCapacity} seats`}
                              variant="outlined"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedVehicle && (
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'success.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      Selected Vehicle
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <CarIcon color="success" />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedVehicle.plateNumber}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedVehicle.make} {selectedVehicle.model} {selectedVehicle.year}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <TextField
                fullWidth
                label="Driver Name"
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                placeholder="Enter driver name (optional)"
                disabled={loading}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments (Optional)"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Add assignment notes or special instructions..."
                disabled={loading}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleApprove} 
          variant="contained" 
          color="success"
          disabled={loading || !selectedVehicleId || availableVehicles.length === 0}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Assigning...' : 'Assign Vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const RejectionDialog: React.FC<RejectionDialogProps> = ({
  open,
  onClose,
  onReject,
  requestDetails,
  loading = false
}) => {
  const [reason, setReason] = useState('');

  const handleReject = () => {
    if (!reason.trim()) return;
    onReject(reason);
    setReason('');
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reject Request</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You are about to reject this vehicle request. Please provide a reason.
          </Alert>
          
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Request Details
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Requester:</strong> {requestDetails.requester}
                </Typography>
                <Typography variant="body2">
                  <strong>Purpose:</strong> {requestDetails.purpose}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <TextField
            fullWidth
            required
            multiline
            rows={4}
            label="Rejection Reason *"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please explain why this request is being rejected..."
            disabled={loading}
            error={reason.trim() === '' && reason.length > 0}
            helperText="This reason will be visible to the requester"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button 
          onClick={handleReject} 
          variant="contained" 
          color="error"
          disabled={loading || !reason.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Rejecting...' : 'Reject Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};